/**
 * Вкладка «Типы номеров» раздела «Номера» менеджера (manager v3 §4 —
 * управление номерным фондом: типы + фото + вместимость + цена + удобства).
 *
 * База (для всех ролей) — витрина каталога GET /v1/public/{slug}/room-types
 * (id, имя, описание, цена, вместимость, удобства, фото). Поверх неё для
 * управляющих ролей (canManage) добавлены CRUD-действия: «+ Добавить тип»
 * и «Редактировать» на карточке (POST/PATCH /v1/rooms/types, гейт mgmt #331).
 * tenant_id для создания/правки берётся из /v1/me.
 *
 * i18n: удобства мапятся в русский (amenityLabels), цена через formatMoney.
 * Мобайл 375: карточки в одну колонку (cat-grid авто-обвал).
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { PublicRoomType, RoomTypeView } from '@hsc/types';
import { resolveTenantSlug } from '../../lib/tenant-slug.js';
import { amenityLabels, formatMoney, photoUrl } from '../../lib/fleet-format.js';
import { useMe } from '../../shared/api/useMe.js';
import { useRoomTypes } from './useRooms.js';
import { DbIcon } from '../../shared/nav/DbIcon.js';
import { iconMap, useAmenities, useIcons } from './useAmenities.js';
import { RoomTypeForm } from './RoomTypeForm.js';
import { PhotoLightbox } from './PhotoLightbox.js';

export interface RoomTypesPanelProps {
  canManage?: boolean;
}

/** Открытая CRUD-форма типа: создание или правка конкретного типа. */
type Editing = { mode: 'create' } | { mode: 'edit'; rt: RoomTypeView } | null;

/**
 * Прообраз RoomTypeView из публичной витрины для префилла формы правки.
 * Витрина не отдаёт раздельную вместимость взрослые/дети — берём capacity_max
 * как взрослых (менеджер поправит), цену — минимальную из диапазона.
 */
function toEditableType(rt: PublicRoomType): RoomTypeView {
  return {
    id: rt.id,
    tenant_id: '',
    code: rt.code,
    display_name: { ru: rt.display_name?.ru ?? rt.name },
    description: { ru: rt.description ?? '' },
    base_price_kopecks: rt.min_price_kopecks,
    currency: rt.currency,
    capacity_adults: rt.capacity_max,
    capacity_children: 0,
    capacity_extra_beds: 0,
    amenities: rt.amenities ?? [],
    status: '',
    created_at: '',
    updated_at: '',
  };
}

export function RoomTypesPanel({ canManage = false }: RoomTypesPanelProps): JSX.Element {
  const slug = useMemo(() => resolveTenantSlug(), []);
  const { data, isLoading, isError } = useRoomTypes(slug);
  const me = useMe();
  const tenantId = me.data?.tenant_id;
  const [editing, setEditing] = useState<Editing>(null);

  const header = (
    <div className="fleet-drawer__head">
      <h1 className="hsc-page__title">Типы номеров</h1>
      {canManage && editing === null && (
        <Button size="sm" onClick={() => setEditing({ mode: 'create' })} data-testid="room-type-create-open">
          + Добавить тип
        </Button>
      )}
    </div>
  );

  // Редактирование/создание типа — ОТДЕЛЬНЫЙ вид (форма вместо списка), а не
  // расширение сверху. Возврат — кнопкой «Назад» в форме (onDone).
  if (canManage && editing !== null) {
    return (
      <section data-testid="room-types-panel">
        <RoomTypeForm
          tenantId={tenantId}
          editing={editing.mode === 'edit' ? editing.rt : undefined}
          onDone={() => setEditing(null)}
        />
      </section>
    );
  }

  if (isLoading) {
    return (
      <section data-testid="room-types-panel">
        {header}
        <div className="cat-grid" data-testid="room-types-skeleton" aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={220} radius={20} />
          ))}
        </div>
      </section>
    );
  }
  if (isError || !data) {
    return (
      <section data-testid="room-types-panel">
        {header}
        <Card>
          <p role="alert">Не удалось загрузить типы номеров. Попробуйте обновить страницу.</p>
        </Card>
      </section>
    );
  }
  if (data.room_types.length === 0) {
    return (
      <section data-testid="room-types-panel">
        {header}
        <Card>
          <p>
            {canManage
              ? 'Типы номеров пока не заведены. Нажмите «Добавить тип», чтобы создать первый.'
              : 'Типы номеров пока не заведены.'}
          </p>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="room-types-panel">
      {header}
      <p className="ta-muted">Всего типов: {data.room_types.length}</p>
      <div className="cat-grid" data-testid="room-types-grid">
        {data.room_types.map((rt) => (
          <RoomTypeCard
            key={rt.id}
            rt={rt}
            canManage={canManage}
            onEdit={() => setEditing({ mode: 'edit', rt: toEditableType(rt) })}
          />
        ))}
      </div>
    </section>
  );
}

function RoomTypeCard({
  rt,
  canManage,
  onEdit,
}: {
  rt: PublicRoomType;
  canManage: boolean;
  onEdit: () => void;
}): JSX.Element {
  // Все фото типа номера → URL'ы для галереи/лайтбокса (в порядке sort_order).
  const allUrls = useMemo(
    () => (rt.photos ?? []).map((p) => photoUrl(p.object_key)).filter((u): u is string => Boolean(u)),
    [rt.photos],
  );
  const cover = allUrls[0];
  const gallery = allUrls.slice(1, 5);
  const amenitiesQ = useAmenities();
  const iconsQ = useIcons();
  const icons = useMemo(() => iconMap(iconsQ.data), [iconsQ.data]);
  // Код удобства → {название, иконка} из каталога тенанта; неизвестный код —
  // русский label из словаря-фолбэка, без иконки.
  const amenityViews = useMemo(() => {
    const byCode = new Map((amenitiesQ.data ?? []).map((a) => [a.code, a]));
    return (rt.amenities ?? []).map((code) => {
      const cat = byCode.get(code);
      return {
        code,
        label: cat?.name ?? amenityLabels([code])[0],
        icon: cat?.icon_id ? icons.get(cat.icon_id) : undefined,
      };
    });
  }, [amenitiesQ.data, icons, rt.amenities]);
  const name = rt.display_name?.ru ?? rt.name;

  // Индекс открытого в лайтбоксе фото (null — закрыт).
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  return (
    <Card data-testid={`room-type-${rt.code}`}>
      {cover ? (
        <button
          type="button"
          className="rt-card__cover-btn"
          onClick={() => setLightboxIdx(0)}
          aria-label={`Открыть фото: ${name}`}
          data-testid={`room-type-${rt.code}-photo-open`}
        >
          <img
            className="rt-card__cover"
            src={cover}
            alt={name}
            loading="lazy"
            data-testid={`room-type-${rt.code}-photo`}
          />
        </button>
      ) : (
        <div className="rt-card__cover rt-card__cover--empty" aria-hidden="true">
          Нет фото
        </div>
      )}

      {gallery.length > 0 && (
        <div className="rt-card__thumbs" data-testid={`room-type-${rt.code}-thumbs`}>
          {gallery.map((u, i) => (
            <button
              key={u}
              type="button"
              className="rt-card__thumb-btn"
              onClick={() => setLightboxIdx(i + 1)}
              aria-label={`Открыть фото ${i + 2}`}
              data-testid={`room-type-${rt.code}-thumb-${i + 1}`}
            >
              <img src={u} alt={`${name} — фото ${i + 2}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {lightboxIdx !== null && allUrls.length > 0 && (
        <PhotoLightbox
          photos={allUrls}
          index={lightboxIdx}
          title={name}
          onClose={() => setLightboxIdx(null)}
          data-testid={`room-type-${rt.code}-lightbox`}
        />
      )}

      <div className="rt-card__head">
        <h3 className="anon__room-name">{name}</h3>
        <span className="fleet-badge fleet-badge--muted" data-testid={`room-type-${rt.code}-rooms`}>
          {rt.total_rooms_count} номеров
        </span>
      </div>

      {rt.description && <p className="anon__room-desc">{rt.description}</p>}

      <p className="anon__room-meta">Гостей: до {rt.capacity_max}</p>

      {amenityViews.length > 0 && (
        <ul className="anon__room-amenities" data-testid={`room-type-${rt.code}-amenities`}>
          {amenityViews.map((a) => (
            <li key={a.code} className="anon__room-amenity">
              <span className="fleet-amenities-pick__icon">
                <DbIcon icon={a.icon} size={16} />
              </span>
              <span>{a.label}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="anon__room-price">
        от {formatMoney(rt.min_price_kopecks, rt.currency)} / ночь
      </p>

      {canManage && (
        <div className="fleet-card__actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            data-testid={`room-type-${rt.code}-edit`}
          >
            Редактировать
          </Button>
        </div>
      )}
    </Card>
  );
}
